import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { debounce } from 'lodash';
import styles from './style.module.css';

const CustomAsyncSelect = ({
  title,
  placeholder,
  value,
  onChange,
  loadOptions,
  cacheOptions = false,
  defaultOptions = false,
  multi = false,
  width,
  minWidth,
  required,
  font = false,
  fontSize,
  dropdown = false,
  height,
  maxHeight,
  mini,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dropdownPos, setDropdownPos] = useState({
    top: 0,
    left: 0,
    width: null,
  });
  const [optionClicked, setOptionClicked] = useState(false);
  const phoneDevice = window.innerWidth < 640;
  const smallDevice = window.innerWidth <= 1440;
  const mediumDevice = window.innerWidth < 1650;
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const mainScroll = document.getElementsByTagName('main')[0];

  // Debounced function to load options
  const debouncedLoadOptions = useRef(
    debounce(async (inputValue) => {
      if (!inputValue || inputValue.length < 2) {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        const results = await loadOptions(inputValue);
        setOptions(Array.isArray(results) ? results : []);
      } catch (err) {
        console.error('Error loading options:', err);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300)
  ).current;

  useEffect(() => {
    if (searchTerm) {
      debouncedLoadOptions(searchTerm);
    }
  }, [searchTerm, debouncedLoadOptions]);

  // Load default options if specified
  useEffect(() => {
    if (defaultOptions && !options.length) {
      debouncedLoadOptions('');
    }
  }, [defaultOptions, debouncedLoadOptions, options.length]);

  const handleOptionClick = (opt) => {
    setOptionClicked(true);
    if (multi) {
      const selectedValues = Array.isArray(value) ? value : [];
      if (isSelected(opt)) {
        const newValues = selectedValues.filter((v) => v.value !== opt.value);
        onChange(newValues);
      } else {
        onChange([...selectedValues, opt]);
      }
    } else {
      onChange(opt);
      setSearchTerm('');
      if (dropdown) {
        setIsOpen(false);
      }
    }
    setTimeout(() => setOptionClicked(false), 100);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdownElement = document.querySelector(`.${styles.options}`);
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        dropdownElement &&
        !dropdownElement.contains(event.target) &&
        !optionClicked
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mouseup', handleClickOutside);
    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
    };
  }, [optionClicked]);

  const selectedValues = multi
    ? Array.isArray(value)
      ? value
      : []
    : value
    ? [value]
    : [];

  const isSelected = (opt) =>
    selectedValues?.some((v) => v?.value === opt?.value);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setError('');

    // If user clears the input completely and we have a selected value, clear the selection
    if (dropdown && !multi && newValue === '' && value) {
      onChange(null);
    }
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter') {
      e.preventDefault();
      setIsOpen(false);
      if (inputRef?.current) inputRef.current.blur();
    } else if (e?.key === 'Backspace') {
      if (multi && searchTerm.length === 0 && selectedValues.length > 0) {
        e.preventDefault();
        const newValues = [...selectedValues];
        newValues.pop();
        onChange(newValues);
      }
    }
  };

  const displayValue = () => {
    if (!multi) {
      // When dropdown is true, show searchTerm if user is typing, otherwise show selected value
      if (dropdown) {
        return searchTerm || value?.label || '';
      }
      return searchTerm || value?.label || '';
    }
    const prefix = selectedValues?.length
      ? selectedValues?.map((v) => v?.label)?.join(', ') + ', '
      : '';
    return prefix + searchTerm;
  };

  const openDropdown = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();

      setDropdownPos({
        top: rect.bottom + mainScroll.scrollTop,
        left: rect.left + mainScroll.scrollLeft,
        width: rect.width,
      });
      setIsOpen(true);
    }
  };

  const renderDropdown = () => {
    if (!isOpen || !dropdownPos.width) return null;

    return createPortal(
      <div
        className={`${styles.options} ${
          options?.length > 6 ? styles.scrollOptions : ''
        }`}
        style={{
          position: 'absolute',
          top: dropdownPos.top + (mini ? -10 : +5),
          left: dropdownPos.left - (smallDevice ? 7 : mediumDevice ? 8.5 : 10),
          width:
            dropdownPos.width +
            (phoneDevice
              ? dropdownPos.width * 0.04
              : smallDevice
              ? dropdownPos.width * 0.08
              : dropdownPos.width * 0.06),
        }}
        onClick={(e) => e.stopPropagation()}>
        {loading ? (
          <div className={styles.loadingOption}>Loading...</div>
        ) : options?.length > 0 ? (
          options?.map((opt) => (
            <div
              key={opt?.value}
              className={`${styles.option} ${
                isSelected(opt) ? styles.selected : ''
              }`}
              onClick={(e) => {
                e.stopPropagation();
                handleOptionClick(opt);
              }}>
              {multi && (
                <span
                  className={`${styles.customCheckbox} ${
                    isSelected(opt) ? styles.checked : ''
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOptionClick(opt);
                  }}
                  style={{ marginRight: '8px' }}>
                  {isSelected(opt) ? (
                    <svg width="16" height="16" viewBox="0 0 16 16">
                      <rect width="16" height="16" rx="4" fill="#2563eb" />
                      <path
                        d="M4 8.5L7 11.5L12 6.5"
                        stroke="white"
                        strokeWidth="2"
                        fill="none"
                      />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 16 16">
                      <rect
                        width="16"
                        height="16"
                        rx="4"
                        fill="#fff"
                        stroke="transparent"
                        strokeWidth="2"
                      />
                    </svg>
                  )}
                </span>
              )}
              {opt?.label}
            </div>
          ))
        ) : (
          <div className={styles.option} style={{ color: '#999' }}>
            No options found
          </div>
        )}
      </div>,
      document.body
    );
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => setIsOpen(false);
    const handleScroll = () => {
      // Close dropdown when bottom of input is less than 64px from top of viewport
      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();

        if (rect.bottom < mainScroll.clientHeight) {
          setIsOpen(false);
        }
      }
    };

    // Add scroll listeners to all scrollable parents
    const scrollableElements = [];
    let element = wrapperRef.current?.parentElement;

    while (element && element !== document.body) {
      const style = window.getComputedStyle(element);
      if (
        style.overflow === 'auto' ||
        style.overflow === 'scroll' ||
        style.overflowY === 'auto' ||
        style.overflowY === 'scroll'
      ) {
        element.addEventListener('scroll', handleScroll, { passive: true });
        scrollableElements.push(element);
      }
      element = element.parentElement;
    }

    window.addEventListener('resize', handleResize);
    mainScroll.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('resize', handleResize);
      mainScroll.removeEventListener('scroll', handleScroll, true);
      scrollableElements.forEach((el) => {
        el.removeEventListener('scroll', handleScroll);
      });
    };
  }, [isOpen, mainScroll]);

  return dropdown ? (
    <>
      <div
        className={styles.wrapper}
        ref={wrapperRef}
        style={{ width: width, minWidth: minWidth }}>
        {title && (
          <span className={font ? styles.biglabel : styles.label}>
            {title}
            {required && <span className={styles.requiredField}>*</span>}
            {required && <span className={styles.requiredText}>Required</span>}
          </span>
        )}

        <div
          className={`${font ? styles.selectBoxFont : styles.selectBox} ${
            isOpen ? styles.focused : ''
          } ${error ? styles.error : ''} ${className || ''}`}
          style={{ height: height, maxHeight: maxHeight }}
          onClick={() => {
            if (!isOpen) {
              openDropdown();
            } else {
              setIsOpen(false);
            }
          }}>
          <input
            ref={inputRef}
            type="text"
            className={font ? styles.bigInput : styles.input}
            placeholder={!displayValue()?.length ? placeholder : ''}
            value={displayValue()}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            style={{ fontSize: `${fontSize}` }}
          />
          {/* <span className={styles.arrow}></span> */}
        </div>

        {error && <div className={styles.errorMessage}>{error}</div>}
      </div>

      {renderDropdown()}
    </>
  ) : (
    <div className={styles.wrapper} ref={wrapperRef} style={{ width: width }}>
      {title && (
        <span className={font ? styles.biglabel : styles.label}>
          {title}
          {required && <span className={styles.requiredField}>*</span>}
          {required && <span className={styles.requiredText}>Required</span>}
        </span>
      )}
      <div
        className={`${styles.selectBox} ${isOpen ? styles.focused : ''} ${
          error ? styles.error : ''
        } ${className || ''}`}
        onClick={() => {
          setIsOpen(!isOpen);
        }}>
        <input
          ref={inputRef}
          type="text"
          className={font ? styles.bigInput : styles.input}
          placeholder={!displayValue()?.length ? placeholder : ''}
          value={displayValue()}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          style={{ fontSize: `${fontSize} !important` }}
        />
        {/* <span className={styles.arrow}></span> */}
      </div>
      {error && <div className={styles.errorMessage}>{error}</div>}
      {isOpen && (
        <div
          className={`${styles.options} ${
            options?.length > 6 ? styles.scrollOptions : ''
          }`}
          style={{
            position: 'absolute',
            width: '-webkit-fill-available',
          }}
          onClick={(e) => e.stopPropagation()}>
          {loading ? (
            <div className={styles.loadingOption}>Loading...</div>
          ) : options?.length > 0 ? (
            options?.map((opt) => (
              <div
                key={opt?.value}
                className={`${styles.option} ${
                  isSelected(opt) && !multi ? styles.selected : ''
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOptionClick(opt);
                }}>
                {multi && (
                  <span
                    className={`${styles.customCheckbox} ${
                      isSelected(opt) ? styles.checked : ''
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOptionClick(opt);
                    }}
                    style={{ marginRight: '8px' }}>
                    {isSelected(opt) ? (
                      <svg width="16" height="16" viewBox="0 0 16 16">
                        <rect width="16" height="16" rx="4" fill="#2563eb" />
                        <path
                          d="M4 8.5L7 11.5L12 6.5"
                          stroke="white"
                          strokeWidth="2"
                          fill="none"
                        />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 16 16">
                        <rect
                          width="16"
                          height="16"
                          rx="4"
                          fill="#fff"
                          stroke="transparent"
                          strokeWidth="2"
                        />
                      </svg>
                    )}
                  </span>
                )}
                {opt?.label}
              </div>
            ))
          ) : (
            <div className={styles.option} style={{ color: '#999' }}>
              No options found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomAsyncSelect;
